/**
 * Définition centralisée des formats de sortie du fichier CSV produit par le job
 * d'enrichment. La valeur `value` correspond au champ `output_format` envoyé à
 * l'API lors de la création d'une demande.
 *
 * Les libellés et descriptions sont regroupés ici (plutôt que codés en dur dans
 * les composants) afin de faciliter une future externalisation i18n.
 *
 * ⚠️ Depuis la migration ClickHouse (EA-103), `per_segment_score` n'est plus
 * commandable : le job le refuse au démarrage. La valeur est **conservée** dans
 * le type et dans la liste — marquée `selectable: false` — parce que les
 * demandes déjà archivées la portent et doivent rester lisibles dans
 * l'historique. Seul le sélecteur du formulaire filtre sur ce drapeau.
 */

export type OutputFormat = 'aggregated' | 'per_segment_score' | 'per_segment_binary';

/** Format pré-sélectionné par défaut dans le formulaire de création. */
export const DEFAULT_OUTPUT_FORMAT: OutputFormat = 'aggregated';

export interface OutputFormatOption {
  value: OutputFormat;
  /** Libellé affiché dans l'interface (FR). */
  label: string;
  /** Description courte affichée sous le libellé. */
  description: string;
  /**
   * `false` → l'option reste affichable dans l'historique (libellé résolu par
   * `getOutputFormatOption`), mais n'est plus proposée à la création.
   * Absent = sélectionnable.
   */
  selectable?: boolean;
  /** Extrait visuel inline (mini-tableau) pour faciliter la comparaison. */
  preview: {
    headers: string[];
    rows: string[][];
  };
}

export const OUTPUT_FORMAT_OPTIONS: OutputFormatOption[] = [
  {
    value: 'aggregated',
    label: 'Score agrégé',
    description:
      'Une colonne « score » unique, à 1 pour chaque contact livré. Format le plus compact : il indique qu’un contact appartient à la sélection, sans détailler quel segment.',
    preview: {
      headers: ['crm_id', 'score'],
      rows: [
        ['A001', '1'],
        ['A002', '1'],
        ['A003', '1'],
      ],
    },
  },
  {
    value: 'per_segment_score',
    label: 'Score par segment',
    description:
      'Seul le segment dominant (au score le plus élevé) conserve son score (entre 0 et 20), les autres colonnes sont à 0.',
    // Plus commandable depuis la migration ClickHouse (EA-103) : le job refuse
    // cette valeur au démarrage. Conservée pour l'affichage de l'historique.
    selectable: false,
    preview: {
      headers: ['crm_id', 'seg_1', 'seg_2', 'seg_3'],
      rows: [
        ['A001', '0', '15', '0'],
        ['A002', '8', '0', '0'],
        ['A003', '20', '0', '0'],
      ],
    },
  },
  {
    value: 'per_segment_binary',
    label: 'Score par segment (binaire 0/1)',
    description:
      'Une colonne par segment ; un seul segment est marqué à 1 par contact. En cas d’égalité — le cas courant, tous les scores valant 1 — c’est le segment le moins volumineux qui l’emporte, puis l’ordre de sélection.',
    preview: {
      headers: ['crm_id', 'seg_1', 'seg_2', 'seg_3'],
      rows: [
        ['A001', '0', '1', '0'],
        ['A002', '1', '0', '0'],
        ['A003', '1', '0', '0'],
      ],
    },
  },
];

/**
 * Formats réellement proposés à la création d'une demande.
 * `OUTPUT_FORMAT_OPTIONS` reste la liste complète, utilisée en lecture
 * (historique, détail d'une demande passée).
 */
export const SELECTABLE_OUTPUT_FORMAT_OPTIONS: OutputFormatOption[] =
  OUTPUT_FORMAT_OPTIONS.filter((option) => option.selectable !== false);

/** Retrouver une option par sa valeur API. */
export const getOutputFormatOption = (
  value?: string
): OutputFormatOption | undefined =>
  OUTPUT_FORMAT_OPTIONS.find((option) => option.value === value);

/**
 * Libellé FR d'un format de sortie pour un affichage en lecture seule.
 * Retourne la valeur brute si elle est inconnue, ou un tiret si absente.
 */
export const getOutputFormatLabel = (value?: string): string => {
  if (!value) return '—';
  return getOutputFormatOption(value)?.label ?? value;
};
